import { TZDate } from '@date-fns/tz';

/**
 * Parse an English-formatted date and time string into a JavaScript Date object.
 *
 * The backend always sends dates as English strings (e.g. "Mar 23, 2026")
 * and times as English strings (e.g. "1:00 pm") regardless of the user's locale.
 * Native `new Date()` reliably parses English date strings in all modern browsers.
 *
 * @param {string} dateStr - Date in English format, e.g. "Mar 23, 2026"
 * @param {string} timeStr - Time in English format, e.g. "1:00 pm"
 * @returns {Date} A Date object in the browser's local timezone
 */
export const parseEnglishDateTime = (dateStr, timeStr) => {
  const combined = `${dateStr.trim()} ${timeStr.trim()}`;
  const parsed = new Date(combined);

  if (Number.isNaN(parsed.getTime())) {
    throw new Error(`Unable to parse date/time: "${combined}"`);
  }

  return parsed;
};

/**
 * Parse an English-formatted date+time string as if it occurred in a specific
 * IANA timezone, returning a TZDate that represents the correct instant.
 *
 * Uses @date-fns/tz's TZDate to construct the date directly in the target
 * timezone, avoiding locale-sensitive moment.js parsing that breaks for
 * non-Latin locales (e.g. ja, zh).
 *
 * @param {string} dateStr  - Date in English format, e.g. "Mar 23, 2026"
 * @param {string} timeStr  - Time in English format, e.g. "1:00 pm"
 * @param {string} timezone - IANA timezone, e.g. "America/Chicago"
 * @returns {TZDate} A TZDate representing the correct instant in the given timezone
 */
export const parseDateInTimezone = (dateStr, timeStr, timezone) => {
  const local = parseEnglishDateTime(dateStr, timeStr);

  return TZDate.tz(
    timezone,
    local.getFullYear(),
    local.getMonth(),
    local.getDate(),
    local.getHours(),
    local.getMinutes()
  );
};
